import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StoreEvent } from "src/interfaces/event.interface";
import { RootState } from "src/store";
import { useEventsByStoreCorrelationId } from "src/features/Events/hooks/useEventsByStoreCorrelationId";
import { useDeleteEvent } from "src/features/Events/hooks/useDeleteEvent";
import Spinner from "src/components/common/Spinner";
import DeleteItemModal from "src/components/common/DeleteItemModal";
import ManageEventModal from "src/features/Events/components/ManageEventModal";
import EventsHeader from "src/features/Events/components/EventsHeader";
import EventsTable from "../features/Events/components/EventsTable";
import { search_events, set_events_list_active_filter } from "src/store/features/events-slice";
import { MENU_LIST_FILTER_TABS, type MenuListActiveFilter } from "src/constants/menuListActiveFilter";

const EventsPage = () => {
  const [eventToEdit, setEventToEdit] = useState<StoreEvent | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<MenuListActiveFilter>("ACTIVE");
  const searchTermRef = useRef("");
  const dispatch = useDispatch();

  const { filteredEvents, toBeDeletedEventId } = useSelector((state: RootState) => state.events);

  const { isEventsLoading, fetchEventsByStoreCorrelationId } = useEventsByStoreCorrelationId();

  const refetchEvents = useCallback(async () => {
    await fetchEventsByStoreCorrelationId(activeFilter);
  }, [activeFilter, fetchEventsByStoreCorrelationId]);

  const { handleCloseDeleteModal, deleteEventMutation, isDeletingEvent, handleOpenDeleteModal } =
    useDeleteEvent({ onDeleted: refetchEvents });

  const handleSearch = useCallback(
    (value: string) => {
      searchTermRef.current = value;
      dispatch(search_events(value));
    },
    [dispatch]
  );

  const handleAddEvent = useCallback(() => {
    setEventToEdit(null);
    setIsEventModalOpen(true);
  }, []);

  const handleEditEvent = useCallback((event: StoreEvent) => {
    setEventToEdit(event);
    setIsEventModalOpen(true);
  }, []);

  const handleCloseEventModal = useCallback(() => {
    setIsEventModalOpen(false);
  }, []);

  useEffect(() => {
    dispatch(set_events_list_active_filter(activeFilter));
    let cancelled = false;
    (async () => {
      await fetchEventsByStoreCorrelationId(activeFilter);
      if (!cancelled) {
        dispatch(search_events(searchTermRef.current));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeFilter, dispatch, fetchEventsByStoreCorrelationId]);

  const statusFilterSlot = (
    <div
      className="flex flex-wrap items-center gap-1 rounded-lg border border-[#F1F1F2] p-0.5 bg-white w-full sm:w-fit"
      role="group"
      aria-label="Filter events by status"
    >
      {MENU_LIST_FILTER_TABS.map(({ id, label }) => {
        const selected = activeFilter === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => setActiveFilter(id)}
            className={`h-[38px] px-3 text-xs font-medium rounded-md transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 flex-1 sm:flex-none min-w-0 ${
              selected
                ? "bg-[#FD7657] text-white shadow-sm"
                : "bg-white text-[#6b7280] hover:bg-[#FEF5F4] hover:text-[#FD7657]"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="w-full flex-1 flex flex-col min-h-0 min-w-0">
      <div className="flex w-full min-w-0 shrink-0 flex-col gap-3 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 mb-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <EventsHeader statusFilterSlot={statusFilterSlot} onSearchChange={handleSearch} />
        <div className="flex w-full sm:w-auto justify-end shrink-0">
          <button
            type="button"
            onClick={handleAddEvent}
            className="h-8 px-4 rounded-lg text-sm font-medium text-white bg-[#FD7657] hover:bg-[#e45e41] w-full sm:w-auto transition-colors"
          >
            Add Event
          </button>
        </div>
      </div>

      {isEventsLoading ? (
        <div className="flex flex-1 min-h-[12rem] items-center justify-center">
          <Spinner />
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="flex flex-1 min-h-[12rem] items-center justify-center px-4">
          <p className="text-[#6b7280] text-center text-sm">
            No events. Click &quot;Add Event&quot; to create one.
          </p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 min-w-0 flex flex-col">
          <EventsTable events={filteredEvents} onEdit={handleEditEvent} onDelete={handleOpenDeleteModal} />
        </div>
      )}

      <ManageEventModal
        eventToEdit={eventToEdit}
        isEventModalOpen={isEventModalOpen}
        handleCloseEventModal={handleCloseEventModal}
        onEventsMutated={refetchEvents}
      />

      <DeleteItemModal
        itemType="Event"
        isDeletingItem={isDeletingEvent}
        isOpen={!!toBeDeletedEventId}
        onClose={handleCloseDeleteModal}
        onDelete={deleteEventMutation}
      />
    </div>
  );
};

export default EventsPage;
